import { memo } from 'react';
import PropTypes from 'prop-types';

const PROGRESS_TONE_STYLES = {
  green: {
    bar: 'bg-emerald-400',
    label: 'text-emerald-400',
  },
  amber: {
    bar: 'bg-amber-400',
    label: 'text-amber-400',
  },
  red: {
    bar: 'bg-red-400',
    label: 'text-red-400',
  },
  blue: {
    bar: 'bg-blue-400',
    label: 'text-blue-400',
  },
  purple: {
    bar: 'bg-purple-400',
    label: 'text-purple-400',
  },
  neutral: {
    bar: 'bg-content-muted',
    label: 'text-content-muted',
  },
};

function normalizeProgressValue(value) {
  if (value == null || value === '') return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return Math.min(100, Math.max(0, numericValue));
}

/**
 * Determinate horizontal progress bar for scores, confidence, completion, etc.
 *
 * @param {object} props
 * @param {number|string} props.value Value clamped to 0-100.
 * @param {import('react').ReactNode} [props.label] Label shown at the end. Defaults to rounded percent.
 * @param {'green'|'amber'|'red'|'blue'|'purple'|'neutral'} [props.tone='green']
 * @param {boolean} [props.showLabel=true]
 * @param {string} [props.ariaLabel='Progress']
 * @param {string} [props.className]
 * @param {string} [props.trackClassName]
 * @param {string} [props.barClassName]
 * @param {string} [props.labelClassName]
 */
const ProgressBar = ({
  value,
  label,
  tone = 'green',
  showLabel = true,
  ariaLabel = 'Progress',
  className = '',
  trackClassName = '',
  barClassName = '',
  labelClassName = '',
}) => {
  const normalizedValue = normalizeProgressValue(value);
  if (normalizedValue == null) return null;

  const toneStyles = PROGRESS_TONE_STYLES[tone] ?? PROGRESS_TONE_STYLES.green;
  const displayLabel = label ?? `${Math.round(normalizedValue)}%`;
  const valueText = typeof displayLabel === 'string' || typeof displayLabel === 'number'
    ? String(displayLabel)
    : undefined;

  return (
    <div
      className={['flex min-w-0 items-center gap-3', className].filter(Boolean).join(' ')}
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={normalizedValue}
      aria-valuetext={valueText}
    >
      <div
        className={[
          'h-1 w-full min-w-0 overflow-hidden rounded-full bg-surface-muted dark:bg-gray-950/70',
          trackClassName,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div
          className={['h-full rounded-full', toneStyles.bar, barClassName].filter(Boolean).join(' ')}
          style={{ width: `${normalizedValue}%` }}
        />
      </div>
      {showLabel ? (
        <span
          className={[
            'shrink-0 text-[11px] font-semibold tabular-nums leading-none',
            toneStyles.label,
            labelClassName,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {displayLabel}
        </span>
      ) : null}
    </div>
  );
};

ProgressBar.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.node,
  tone: PropTypes.oneOf(['green', 'amber', 'red', 'blue', 'purple', 'neutral']),
  showLabel: PropTypes.bool,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  trackClassName: PropTypes.string,
  barClassName: PropTypes.string,
  labelClassName: PropTypes.string,
};

export default memo(ProgressBar);
